import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { Box, useTheme } from '@mui/material';
import React from 'react';
import { Control, Controller, UseFormSetValue } from 'react-hook-form';
import { AfterPeriodContent } from '../../molecules/AfterPeriodContent/AfterPeriodContent';
import { RetentionPeriodToggle } from '../../molecules/RetentionPeriodToggle/RetentionPeriodToggle';
import { SectionHeader } from '../../molecules/SectionHeader/SectionHeader';
import type { FormValues } from '../../organisms/AddRuleDrawer/useAddRuleDrawer';
import { RetentionMode } from '../../utils';

interface Props {
  control: Control<FormValues>;
  mode: FormValues['mode'];
  setValue: UseFormSetValue<FormValues>;
  periodValue: FormValues['periodValue'];
  periodUnit: FormValues['periodUnit'];
  anchorDate: FormValues['anchorDate'];
}

export const RetentionPeriodSection: React.FC<Props> = ({
  control,
  mode,
  setValue,
  periodValue,
  periodUnit,
  anchorDate,
}) => {
  const theme = useTheme<LensTheme>();
  const sp = theme.tokens.semantic.spacing.desktop.gap;
  const color = theme.tokens.semantic.color;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: sp[3].value,
        paddingTop: sp[4].value,
        borderTop: `1px solid ${color.ui.divider.default.value}`,
      }}
    >
      <SectionHeader
        title="Retention period"
        subtitle="Specify when the actions should be applied to cases that match the conditions"
      />
      <Controller
        name="mode"
        control={control}
        render={({ field }) => (
          <RetentionPeriodToggle
            checked={field.value === RetentionMode.AFTER_PERIOD}
            disabled={field.value === RetentionMode.IMMEDIATE}
            title="After a Specific Period"
            subtitle="Actions will be applied after a set time from a reference date."
            onChange={() =>
              field.onChange(
                field.value === RetentionMode.AFTER_PERIOD
                  ? null
                  : RetentionMode.AFTER_PERIOD,
              )
            }
          >
            {mode === RetentionMode.AFTER_PERIOD && (
              <AfterPeriodContent
                periodValue={periodValue}
                setPeriodValue={(value) => setValue('periodValue', value)}
                periodUnit={periodUnit}
                setPeriodUnit={(value) => setValue('periodUnit', value)}
                anchorDate={anchorDate}
                setAnchorDate={(value) => setValue('anchorDate', value)}
              />
            )}
          </RetentionPeriodToggle>
        )}
      />
      <Controller
        name="mode"
        control={control}
        render={({ field }) => (
          <RetentionPeriodToggle
            checked={field.value === RetentionMode.IMMEDIATE}
            disabled={field.value === RetentionMode.AFTER_PERIOD}
            title="Immediately"
            subtitle="Actions will be applied immediately."
            onChange={() =>
              field.onChange(
                field.value === RetentionMode.IMMEDIATE
                  ? null
                  : RetentionMode.IMMEDIATE,
              )
            }
          />
        )}
      />
    </Box>
  );
};
